import { MutableRefObject, useCallback, useEffect, useRef } from 'react'
import { useArcVoiceCall } from '../contexts/ArcVoiceCallContext'
import { ArcVoiceCallStatus } from '../helpers/arcVoice/arcVoiceCall'
import { HuddleSession } from '../helpers/huddle/huddleSession'

interface IArcInHuddle {
  /** Arc is in this tab's huddle, hosted from this browser. */
  isHostingArc: boolean
  /** A one-to-one Arc Voice call is open, which a huddle can't start over. */
  isOnPrivateArcCall: boolean
  inviteArc: () => void
  dismissArc: () => void
}

interface IArcInHuddleOptions {
  sessionRef: MutableRefObject<HuddleSession | null>
  isInHuddle: boolean
  /** This tab's huddle once it has one; null while joining or out of one. */
  huddleId: string | null
  notify: (message: string, isError: boolean) => void
}

/**
 * Arc in this tab's huddle. Depends on the call's status, never its whole snapshot,
 * so a caption arriving doesn't hand the huddle's consumers a new value.
 */
const useArcInHuddle = ({
  sessionRef,
  isInHuddle,
  huddleId,
  notify,
}: IArcInHuddleOptions): IArcInHuddle => {
  const { start, call, roomHuddleId, snapshot } = useArcVoiceCall()
  const isCallOpen = !!snapshot && snapshot.status !== ArcVoiceCallStatus.Ended
  const isOnPrivateArcCall = isCallOpen && !roomHuddleId
  const isHostingArc = !!huddleId && roomHuddleId === huddleId && isCallOpen

  const inviteArc = useCallback(() => {
    const current = sessionRef.current
    if (!current || !huddleId) return
    if (isCallOpen) {
      notify('Hang up Arc Voice first to bring Arc into the huddle.', true)
      return
    }
    // Arc's turns land in the huddle's own conversation, not a new DM with Arc.
    const started = start(current.getSnapshot().channelId, {
      room: {
        huddleId,
        getRoomAudio: () => current.openRoomAudio(),
        attachArcVoice: (stream) => current.setArcVoice(stream),
        judgeInsight: (lines, arcSaid) => current.judgeArcInsight(lines, arcSaid),
        recordArcLine: (text) => current.addArcLine(text),
        whoIsSpeaking: (windowMs) => current.recentSpeakerUserId(windowMs),
      },
    })
    if (!started) return
    current.setArcIn(true).catch((err: Error) => {
      notify(err?.message || 'Couldn’t bring Arc in.', true)
      void started.end()
    })
  }, [huddleId, isCallOpen, notify, sessionRef, start])

  const dismissArc = useCallback(() => {
    if (isHostingArc) void call?.end()
  }, [call, isHostingArc])

  // Arc hung up (here or from its pill): its voice leaves the room, and the room is told.
  const wasHostingArc = useRef(false)
  useEffect(() => {
    if (wasHostingArc.current && !isHostingArc) {
      sessionRef.current?.setArcVoice(null)
      void sessionRef.current?.setArcIn(false).catch(() => {})
    }
    wasHostingArc.current = isHostingArc
  }, [isHostingArc, sessionRef])

  // Leaving the huddle takes Arc with it: it was only ever there through this browser.
  useEffect(() => {
    if (!isInHuddle && roomHuddleId && isCallOpen) void call?.end()
  }, [call, isCallOpen, isInHuddle, roomHuddleId])

  return { isHostingArc, isOnPrivateArcCall, inviteArc, dismissArc }
}

export default useArcInHuddle
